import { useRef } from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { HERO_VIDEO, HERO_POSTER } from "@/content";

const LINES = ["DAVID", "ROSENBLUM"];

export default function Hero() {
  const ref = useRef(null);
  const reduced = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const videoY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  const reveal = (i) =>
    reduced
      ? {}
      : {
          initial: { y: "112%" },
          animate: { y: 0 },
          transition: { duration: 1.1, delay: 0.2 + i * 0.14, ease: [0.16, 1, 0.3, 1] },
        };

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative h-[100svh] min-h-[560px] w-full overflow-hidden bg-ink text-paper"
    >
      <motion.div style={reduced ? undefined : { y: videoY }} className="absolute inset-0 scale-[1.12]">
        {reduced ? (
          <img src={HERO_POSTER} alt="David Rosenblum leading worship" className="h-full w-full object-cover" />
        ) : (
          <video
            data-testid="hero-video"
            className="h-full w-full object-cover"
            src={HERO_VIDEO}
            poster={HERO_POSTER}
            autoPlay
            muted
            loop
            playsInline
            disablePictureInPicture
            aria-label="Video of David Rosenblum leading worship"
          />
        )}
      </motion.div>
      <div className="absolute inset-0 bg-ink/55" aria-hidden="true" />
      <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-ink/80 to-transparent" aria-hidden="true" />

      <motion.div
        style={reduced ? undefined : { opacity: fade }}
        className="relative z-10 h-full flex flex-col items-center justify-center text-center px-6"
      >
        <div className="relative" data-testid="hero-wordmark">
          <h1 className="font-spartan font-extrabold uppercase leading-[0.88] display-type text-[17vw] sm:text-[13vw] lg:text-[9.5rem]">
            {LINES.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-[0.06em] -mb-[0.06em]">
                <motion.span className="block" {...reveal(i)}>
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>
          <h1
            aria-hidden="true"
            className="pointer-events-none select-none absolute inset-0 translate-x-[0.045em] blur-[6px] opacity-20 font-spartan font-extrabold uppercase leading-[0.88] display-type text-[17vw] sm:text-[13vw] lg:text-[9.5rem]"
          >
            {LINES.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-[0.06em] -mb-[0.06em]">
                <motion.span className="block" {...reveal(i)}>
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>
        </div>

        <motion.p
          data-testid="hero-eyebrow"
          initial={reduced ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.6 }}
          className="text-[11px] sm:text-xs font-medium tracking-[0.4em] uppercase text-paper/70 mt-6"
        >
          Worship Leader + Speaker
        </motion.p>

        <motion.div
          initial={reduced ? false : { opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.75, ease: [0.16, 1, 0.3, 1] }}
          className="mt-10 flex flex-col sm:flex-row items-center gap-4"
        >
          <a
            href="#invite"
            data-testid="hero-invite-button"
            className="bg-paper text-ink font-semibold text-xs tracking-[0.22em] uppercase px-9 py-4 rounded-full hover:bg-sand transition-colors duration-300"
          >
            Invite David
          </a>
          <a
            href="#listen"
            data-testid="hero-listen-button"
            className="border border-paper/60 text-paper font-semibold text-xs tracking-[0.22em] uppercase px-9 py-4 rounded-full hover:bg-paper hover:text-ink transition-colors duration-300"
          >
            Listen
          </a>
        </motion.div>
      </motion.div>

      <motion.a
        href="#about"
        data-testid="hero-scroll-cue"
        aria-label="Scroll to About section"
        initial={reduced ? false : { opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 text-paper/60 hover:text-paper transition-colors duration-300"
      >
        <ArrowDown size={20} className="animate-bounce" />
      </motion.a>
    </section>
  );
}
